import React, { useState, useEffect } from "react"
import { useStaticQuery, graphql, Link as GatsbyLink } from "gatsby"
import { mdiMenu, mdiClose } from "@mdi/js"
import { motion } from "framer-motion"
import css from "@styled-system/css"

import { Box, ColorMode, IconButton, Link, Text, Wrapper } from "components"

let menuOpenTracked = false

function Header({ location }) {
  const { navigation } = useStaticQuery(graphql`
    {
      navigation: prismicNavigation {
        data {
          links {
            label
            internal_link {
              type
              slug
            }
          }
        }
      }
    }
  `)
  const [wash, setWash] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    setMenuOpen(false)
    if (location && location.pathname === "/") {
      setWash(true)
    } else {
      setWash(false)
    }
  }, [location])

  // auto-open and close menu
  const checkScroll = () => {
    if (window.scrollY <= 0 && !menuOpenTracked) {
      menuOpenTracked = true
      setMenuOpen(true)
    }
    if (window.scrollY > 0 && menuOpenTracked) {
      menuOpenTracked = false
      setMenuOpen(false)
    }
  }

  useEffect(() => {
    checkScroll()
    window.addEventListener("scroll", checkScroll)
    return function cleanup() {
      window.removeEventListener("scroll", checkScroll)
    }
  }, [])

  return (
    <Box
      data-component-id="header"
      as="header"
      position="absolute"
      top={["layout.2", "40px"]}
      left={0}
      width="100%"
      zIndex={2}
    >
      <Wrapper pt={0} pb={0} bg={null}>
        {/* Logomark */}
        <Box
          position="fixed"
          top={["layout.1", "layout.4"]}
          left="layout.4"
          height={48}
          width={48}
          zIndex={2}
          css={css({
            "@media screen and (max-width: 1240px)": {
              left: "layout.1",
            },
          })}
          borderRadius={9999}
          bg="background"
        >
          <GatsbyLink to="/">
            <svg
              width={(104 / 117) * 32}
              height={32}
              viewBox="0 0 104 117"
              fill="none"
              css={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-42.5%, -50%)",
              }}
            >
              <title>BluPlanet Recycling</title>
              <path
                d="M51.682 5.552c-15.676 0-29.713 6.989-39.193 18.013V6.216A6.245 6.245 0 006.246 0 6.24 6.24 0 00.005 6.216H0v53.322h.058C.587 71.56 5.22 82.517 12.591 91.037V54.54a.189.189 0 00.008-.053c1.411-20.363 18.362-36.444 39.083-36.444 21.644 0 39.188 17.545 39.188 39.191 0 21.645-17.544 39.19-39.188 39.19a38.982 38.982 0 01-15.714-3.296v13.342a51.45 51.45 0 0015.714 2.447c28.544 0 51.682-23.141 51.682-51.683 0-28.543-23.138-51.682-51.682-51.682z"
                fill={"#00C7EA"}
              />
              <path
                d="M71.367 65.297c-3.178 7.751-10.789 13.214-19.685 13.214-6.245 0-11.844-2.707-15.736-6.991v15.587a33.59 33.59 0 0015.736 3.895c20.944 0 30.686-18.841 32.176-23.527a11.127 11.127 0 01-4.654 1.025 11.188 11.188 0 01-7.837-3.203z"
                fill={"#939497"}
              />
              <path
                d="M72.96 57.284a6.245 6.245 0 006.244 6.216 6.24 6.24 0 006.24-6.216h.004l.002-.05c0-18.649-15.121-33.766-33.768-33.766S17.915 38.585 17.915 57.234v53.32a6.246 6.246 0 006.245 6.245 6.246 6.246 0 006.244-6.245V83.449l.009.007V57.418c0-.062-.009-.122-.009-.184 0-11.751 9.527-21.277 21.278-21.277 11.736 0 21.251 9.502 21.274 21.232a.057.057 0 01.004.007v.088z"
                fill={"#8BC953"}
              />
            </svg>
          </GatsbyLink>
        </Box>

        {/* Wordmark */}
        <Box
          ml={["36px", 0]}
          // borderRadius={9999}
          // bg="background"
        >
          <GatsbyLink to="/">
            <Box
              as="svg"
              width={[(608 / 154) * 40, (608 / 154) * 48]}
              height={[40, 48]}
              viewBox="0 0 608 154"
              fill="none"
              css={{ verticalAlign: "top" }}
            >
              <title>BluPlanet Recycling</title>
              <path
                d="M382.607 121.49c-1.698 1.698-2.754 4.084-2.754 6.745v15.233h-1.835v-25.419h1.835v4.404c.964-1.743 1.836-2.707 3.533-3.716 1.882-1.055 3.487-1.33 5.414-1.376v1.651c-2.064-.046-4.404.643-6.193 2.478zm23.507-2.386c-5.965 0-10.874 4.267-11.655 10.186h23.217c-.78-5.964-5.552-10.186-11.562-10.186zm-11.701 11.93c.046 6.286 5.277 11.424 11.609 11.424 4.771 0 8.35-2.248 10.461-6.514h1.973c-2.202 5.368-6.699 8.304-12.526 8.304-7.524 0-13.306-6.24-13.306-13.214 0-7.846 6.331-13.627 13.536-13.627 7.478 0 13.397 6.194 13.397 13.627h-25.144zm47.03 13.123c-7.342.091-13.628-5.873-13.674-13.215-.092-7.479 6.011-13.535 13.398-13.535 5.873-.046 10.828 3.303 12.847 9.039h-1.881c-1.927-4.727-6.286-7.388-10.966-7.342-6.469.046-11.746 5.552-11.562 11.975.138 6.332 5.368 11.425 11.7 11.425 5.047 0 8.901-2.569 10.828-7.203h1.881c-2.11 5.552-6.652 8.81-12.571 8.856zm26.625 9.68h-1.973l4.175-9.956-9.956-25.832h1.972l9.04 23.355 9.773-23.355h1.927l-14.958 35.788zm35.835-9.68c-7.342.091-13.628-5.873-13.674-13.215-.092-7.479 6.011-13.535 13.398-13.535 5.873-.046 10.828 3.303 12.847 9.039h-1.881c-1.927-4.727-6.286-7.388-10.966-7.342-6.469.046-11.746 5.552-11.562 11.975.137 6.332 5.368 11.425 11.7 11.425 5.047 0 8.901-2.569 10.828-7.203h1.881c-2.11 5.552-6.652 8.81-12.571 8.856zm21.793-.689V108h1.835v35.468h-1.835zm12.941 0v-25.419h1.651v25.419h-1.651zm-.046-31.934V108h1.697v3.534h-1.697zm32.349 31.934v-14.224c0-5.459-4.221-10.278-9.682-10.186-4.68.092-8.58 3.946-8.58 8.672v15.738h-1.789v-25.419h1.743v4.037c2.203-3.12 4.91-4.588 8.764-4.725 6.286-.138 11.379 5.276 11.379 11.516v14.591h-1.835zm22.894-24.272c-6.378.046-11.562 5.276-11.608 11.608 0 6.332 4.955 11.563 11.287 11.792 6.653.275 12.343-5.276 12.251-11.93-.138-6.377-5.552-11.562-11.93-11.47zm.459 34.641c-5.23 0-9.957-2.844-12.205-8.029h1.835c2.066 4.268 6.195 6.47 10.37 6.516 5.69 0 11.517-4.588 11.517-10.691v-4.497c-2.615 4.909-7.25 7.204-12.481 7.112-7.204-.183-12.801-6.332-12.801-13.49.046-7.295 6.24-13.306 13.306-13.351 5.185 0 9.452 2.431 11.976 7.249v-6.607h1.651v23.309c0 7.019-6.102 12.479-13.168 12.479z"
                fill={wash ? "#fff" : "#939497"}
              />
              <path
                d="M73.835 65.987c0 9.796-3.71 18.013-11.13 24.651-7.42 6.639-16.239 9.957-26.457 9.957-10.217 0-18.732-3.328-25.543-9.986C3.568 83.626 0 74.696 0 63.816V0h23.233v64.08c0 4.387 1.256 7.96 3.771 10.722 2.513 2.762 5.757 4.142 9.731 4.142 3.811 0 7.074-1.352 9.792-4.059 2.716-2.704 4.074-5.954 4.074-9.749 0-3.876-1.358-7.248-4.074-10.114-2.718-2.866-6.021-4.299-9.914-4.299-1.459 0-2.919.244-4.379.73l-5.23 1.825V31.139c3.902-1.216 7.682-1.824 11.343-1.824 9.837 0 18.211 3.683 25.122 11.05 6.91 7.367 10.366 15.908 10.366 25.622zm12.469 32.905V0h23.476v98.892H86.304zm102.218-68.847l-.121 41.843c0 8.191-3.448 15.004-10.34 20.436-6.325 4.947-13.34 7.419-21.043 7.419-8.353 0-15.53-2.755-21.53-8.271-6.569-6-9.853-13.867-9.853-23.598V30.045h23.72v39.654c0 2.189.764 4.135 2.292 5.838 1.528 1.704 3.339 2.555 5.434 2.555 5.39 0 8.087-4.176 8.087-12.529V30.045h23.354z"
                fill={wash ? "#fff" : "#00C7EA"}
              />
              <path
                d="M274.834 63.432c0 9.667-3.455 18.197-10.366 25.588-6.91 7.393-15.244 11.089-25 11.089-2.115 0-4.047-.124-5.795-.367-1.749-.246-3.637-.734-5.669-1.468V76.146l5.378 1.912c1.468.509 2.935.764 4.402.764 3.832 0 7.133-1.466 9.904-4.399 2.771-2.93 4.157-6.311 4.157-10.139 0-3.908-1.346-7.207-4.033-9.895-2.688-2.687-5.987-4.031-9.895-4.031-3.83 0-7.047 1.38-9.653 4.136-2.608 2.758-3.91 6.285-3.91 10.583v63.617h-23.232V65.32c0-10.866 3.466-19.685 10.399-26.456 6.934-6.77 15.51-10.158 25.727-10.158 10.136 0 18.934 3.332 26.396 9.992 7.46 6.662 11.19 14.906 11.19 24.734zm12.775 35.46V0h23.476v98.892h-23.476zm109.3 0h-22.381V69.894c0-7.765-1.825-13.184-5.475-16.259-2.27-1.941-5.15-2.912-8.636-2.912-3.406 0-6.549 1.353-9.426 4.058-2.88 2.705-4.319 5.915-4.319 9.628 0 4.037 1.256 7.43 3.771 10.174 2.513 2.745 5.96 4.116 10.339 4.116 1.297 0 2.878-.201 4.744-.607 1.864-.405 3.364-.931 4.501-1.582v22.382c-.569.243-1.502.486-2.798.729-3.487.731-6.447 1.095-8.88 1.095-9.651 0-17.881-3.561-24.692-10.684-6.812-7.124-10.218-15.625-10.218-25.503 0-10.037 3.649-18.415 10.947-25.135 7.299-6.719 16.34-10.079 27.126-10.079 11.027 0 19.827 3.643 26.396 10.928 6 6.639 9.001 14.815 9.001 24.527v34.122zm76.738 0h-23.233V59.116c0-2.433-.654-4.419-1.961-5.96-1.308-1.54-3.312-2.311-6.006-2.311-2.125 0-3.987.832-5.579 2.493-1.593 1.663-2.389 3.631-2.389 5.9v39.654h-23.72l.122-37.83c0-9.649 3.284-17.515 9.853-23.597 6.082-5.596 13.259-8.393 21.53-8.393 7.703 0 14.676 2.432 20.922 7.298 6.973 5.433 10.461 12.285 10.461 20.557v41.965zm84.227-25.514l-2.554 5.614c-2.19 4.965-6.569 9.602-13.137 13.915-7.298 4.801-14.718 7.202-22.26 7.202-10.137 0-18.692-3.574-25.666-10.722s-10.461-15.718-10.461-25.711c0-9.829 3.547-18.155 10.644-24.978 7.095-6.823 15.631-10.235 25.605-10.235 7.459 0 14.19 2.034 20.192 6.099 4.784 3.254 8.514 7.278 11.19 12.075l2.191 3.902-34.667 23.174-9.002-13.567 15.205-10.265c-2.029-1.304-4.056-1.955-6.082-1.955-3.813 0-7.076 1.446-9.792 4.335-2.718 2.889-4.074 6.287-4.074 10.194 0 4.885 1.356 8.977 4.074 12.273 2.716 3.298 6.222 4.945 10.521 4.945 3.324 0 6.813-.98 10.461-2.944 4.38-2.37 7.42-5.476 9.123-9.318l1.217-2.699 17.272 8.666zm49.27-23.993h-15.935V68.765c0 1.455.101 2.665.306 3.635.205.969.717 1.896 1.534 2.784 1.634 1.858 3.636 2.786 6.006 2.786h7.724v20.922h-4.988c-6.975 0-11.84-.283-14.596-.852-5.109-1.052-9.366-3.406-12.772-7.054-2.92-3.163-4.886-6.528-5.899-10.097-1.016-3.567-1.521-7.946-1.521-13.137V11.191h24.206v18.124h15.935v20.07z"
                fill={wash ? "#fff" : "#8BC953"}
              />
            </Box>
          </GatsbyLink>
        </Box>

        {/* Menu */}
        <ColorMode mode="light">
          <motion.div
            variants={{
              initial: {
                width: 48,
                height: 48,
                transition: {
                  duration: 0.3,
                  when: "afterChildren",
                },
              },
              open: {
                width: 256,
                height:
                  48 +
                  (navigation.data.links
                    ? (navigation.data.links.length + 1) * 36
                    : 0),
                transition: {
                  duration: 0.4,
                  when: "beforeChildren",
                },
              },
            }}
            initial="initial"
            animate={menuOpen ? "open" : "initial"}
            css={css({
              position: "fixed",
              top: ["layout.1", "layout.4"],
              right: "layout.4",
              zIndex: 2,
              borderRadius: "24px",
              bg: "text",
              boxShadow: "elevations.2",
              overflow: "hidden",
              "@media screen and (max-width: 1240px)": {
                right: "layout.1",
              },
            })}
          >
            <Box
              position="absolute"
              top="0"
              right="0"
              css={{ "*:focus": { outline: "none" } }}
            >
              <IconButton
                aria-label={menuOpen ? "Close menu" : "Open menu"}
                icon={menuOpen ? mdiClose : mdiMenu}
                intent="default"
                appearance="primary"
                height={48}
                borderRadius="24px"
                onClick={() => {
                  setMenuOpen(!menuOpen)
                }}
              />
            </Box>
            <Box mt="40px" mx="layout.3">
              {navigation.data.links &&
                navigation.data.links.map(
                  ({ label, internal_link, external_link }, index) => (
                    <motion.div
                      key={"navigationLink" + index}
                      variants={{
                        initial: {
                          opacity: 0,
                          y: 10,
                          transition: {
                            duration: 0.2,
                          },
                        },
                        open: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.3,
                            delay: index * 0.075,
                          },
                        },
                      }}
                      initial="initial"
                      css={css({ marginTop: "8px" })}
                    >
                      <Text color="background">
                        <Link
                          to={internal_link ? internal_link : external_link.url}
                        >
                          {label}
                        </Link>
                      </Text>
                    </motion.div>
                  )
                )}
              <motion.div
                variants={{
                  initial: {
                    opacity: 0,
                    y: 10,
                    transition: {
                      duration: 0.2,
                    },
                  },
                  open: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: 0.3,
                      delay: navigation.data.links.length * 0.075,
                    },
                  },
                }}
                initial="initial"
                css={css({ marginTop: "8px" })}
              >
                <Text color="background">
                  <Link
                    to={{ slug: "contact" }}
                    onClick={() => {
                      setTimeout(() => {
                        let quote = document.getElementById("quote")
                        if (quote) {
                          quote.scrollIntoView()
                        }
                      }, 900)
                    }}
                  >
                    Request Proposal
                  </Link>
                </Text>
              </motion.div>
            </Box>
          </motion.div>
        </ColorMode>
      </Wrapper>
    </Box>
  )
}

export default Header
